// ISDP CUSTOM: Sentry frontend initialization — extracted from App.tsx so that file
// stays pure route/plugin wiring. Renders null; runs Sentry.init once inside the React
// tree (where config is available) and attaches the signed-in user by opaque entity ref.
import * as Sentry from '@sentry/browser';
import { useEffect, useRef } from 'react';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';

// Staging and production share one Sentry project, so this tag is the only thing separating
// them. It used to report BOTH deployed environments as 'production', which buried real
// production incidents in staging noise once prod went live. Mirrors the inference the
// backend already does (see modules/sentryModule.ts).
function sentryEnvironment(baseUrl: string): string {
  if (baseUrl.includes('localhost')) return 'development';
  if (/staging/i.test(baseUrl)) return 'staging';
  return 'production';
}

export const SentryInit = () => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const initialized = useRef(false);
  if (!initialized.current) {
    // Frontend Sentry DSN. Sentry frontend DSNs are public (they ship in browser
    // code), so we hardcode it as the reliable default — the app-backend does not
    // serve sentry.frontend.dsn to the browser at runtime (its config schema is
    // collected from the backend's package graph, which excludes packages/app).
    // The config value is still honored if present.
    const FRONTEND_DSN =
      'https://35d272f3429f704d0a168fa039f1c0e3@o4507922417975296.ingest.de.sentry.io/4511608475484240';
    const dsn = config.getOptionalString('sentry.frontend.dsn') ?? FRONTEND_DSN;
    // Never initialize Sentry under test — browserTracingIntegration probes
    // performance APIs jsdom doesn't implement, and we don't want test telemetry.
    if (dsn && process.env.NODE_ENV !== 'test') {
      const baseUrl = config.getOptionalString('app.baseUrl') ?? '';
      // Route Sentry envelopes through our own backend (/api/sentry-tunnel) instead
      // of straight to sentry.io, so ad-blockers don't cancel "Report a bug" sends.
      const tunnel = `${
        config.getOptionalString('backend.baseUrl') ?? ''
      }/api/sentry-tunnel`;
      // Defense-in-depth: strip query strings (may carry tokens) and redact any
      // bearer/PAT tokens from captured URLs/messages before they reach Sentry.
      const TOKEN_RE =
        /(dhpat_|dhsvc_|pat-[a-z0-9]{2}-)[A-Za-z0-9._-]+|Bearer\s+[A-Za-z0-9._-]+/gi;
      Sentry.init({
        dsn,
        tunnel,
        environment: sentryEnvironment(baseUrl),
        beforeSend(event) {
          try {
            if (event.request?.url) {
              event.request.url = event.request.url.split('?')[0];
            }
            if (event.message) {
              event.message = event.message.replace(TOKEN_RE, '[redacted]');
            }
            for (const ex of event.exception?.values ?? []) {
              if (ex.value) ex.value = ex.value.replace(TOKEN_RE, '[redacted]');
            }
          } catch {
            /* never block sending on a scrub failure */
          }
          return event;
        },
        integrations: [
          Sentry.browserTracingIntegration(),
          // ISDP CUSTOM: user "Report a bug" widget. We drive it from our OWN trigger
          // (bottom-left ReportBugButton) instead of Sentry's floating button
          // (autoInject:false) — the default sits bottom-right, colliding with the
          // curator widget. Text-only, no screenshot; and since we only ever attach the
          // opaque userEntityRef to Sentry (GDPR, see setUser below), the form does not
          // collect name/email either.
          Sentry.feedbackIntegration({
            autoInject: false,
            showBranding: false,
            enableScreenshot: false,
            showName: false,
            showEmail: false,
            // Respect the app's light/dark preference; the ODX cyan accent is applied
            // via CSS custom properties on #sentry-feedback (see ReportBugButton.css) —
            // those pierce the widget's shadow DOM, unlike the JS theme options.
            colorScheme: 'system',
            buttonLabel: 'Report a bug',
            formTitle: 'Report a bug',
            submitButtonLabel: 'Send report',
            messageLabel: 'What went wrong?',
            messagePlaceholder:
              'Describe the bug — what you did, what you expected, and what actually happened.',
            successMessageText: 'Thanks — your report was sent to the DevHub team.',
          }),
        ],
        tracesSampleRate: 0.1,
      });
    }
    initialized.current = true;
  }
  // Attach the signed-in user to Sentry events so errors are attributable —
  // by the OPAQUE Backstage entity ref (e.g. user:default/jdoe), NOT the email.
  // Avoids shipping PII to Sentry (GDPR); the ref is still enough to correlate.
  useEffect(() => {
    let active = true;
    identityApi
      .getBackstageIdentity()
      .then(id => {
        if (active && id?.userEntityRef) {
          Sentry.setUser({ id: id.userEntityRef });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [identityApi]);
  return null;
};
